import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { 
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormDescription
} from "@/components/ui/form";
import { CheckCircle, Circle, CircleDashed, X } from "lucide-react";
import type { Database } from "@/integrations/supabase/types";

type OnboardingData = Database['public']['Tables']['onboarding_data']['Row'];
type OnboardingDataInsert = Database['public']['Tables']['onboarding_data']['Insert'];

interface OnboardingFormProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onComplete?: () => void;
  initialData?: OnboardingData | null;
  userId: string | undefined;
  isRegistration?: boolean;
}

type Step = {
  title: string;
  fields: string[];
  description: string;
};

const OnboardingForm: React.FC<OnboardingFormProps> = ({
  isOpen,
  onOpenChange,
  onComplete,
  initialData,
  userId,
  isRegistration = false
}) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();
  
  const form = useForm<OnboardingData>({
    defaultValues: initialData || {
      payment_options: [],
      tax_id: "",
      domain: "",
      target_audience: "",
      ideal_customer: "",
      qualification_questions: "",
      color_scheme: "",
      company_info: "",
      faq: [],
      website_examples: "",
      competitor_websites: "",
      address: ""
    } as Partial<OnboardingData>
  });

  const steps: Step[] = [
    {
      title: "Grundlegende Informationen",
      fields: ["company_info", "address", "domain", "tax_id"],
      description: "Bitte geben Sie grundlegende Informationen zu Ihrem Unternehmen ein."
    },
    {
      title: "Zahlungsoptionen",
      fields: ["payment_options"],
      description: "Welche Zahlungsoptionen möchten Sie Ihren Kunden anbieten?"
    },
    {
      title: "Zielgruppe",
      fields: ["target_audience", "ideal_customer"],
      description: "Beschreiben Sie Ihre Zielgruppe und idealen Kunden."
    },
    {
      title: "Qualifikationsfragen",
      fields: ["qualification_questions"],
      description: "Welche Fragen helfen Ihnen, geeignete Kunden zu identifizieren?"
    },
    {
      title: "Design & Konkurrenz",
      fields: ["color_scheme", "website_examples", "competitor_websites"],
      description: "Informationen zu Design-Präferenzen und Wettbewerbern."
    },
    {
      title: "FAQ",
      fields: ["faq"],
      description: "Häufig gestellte Fragen und Antworten für Ihre Kunden."
    }
  ];

  useEffect(() => {
    if (initialData?.payment_options) {
      form.setValue("payment_options", initialData.payment_options);
    }
    if (initialData?.faq) {
      form.setValue("faq", initialData.faq);
    }
  }, [initialData, form]);

  const handleNext = async () => {
    const currentFields = steps[currentStep].fields;
    const isValid = await form.trigger(currentFields as any);
    
    if (isValid) {
      if (currentStep < steps.length - 1) {
        setCurrentStep(currentStep + 1);
      } else {
        await handleSubmit();
      }
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSubmit = async () => {
    if (!userId) {
      toast.error("Benutzer nicht gefunden. Bitte melden Sie sich an und versuchen Sie es erneut.");
      return;
    }
    
    try {
      setIsSubmitting(true);
      
      const values = form.getValues();
      
      let paymentOptions = values.payment_options;
      if (typeof values.payment_options === 'string') {
        paymentOptions = (values.payment_options as unknown as string).split(',').map(item => item.trim());
      }
      
      let faqItems = values.faq;
      if (typeof values.faq === 'string') {
        faqItems = (values.faq as unknown as string).split('\n\n').filter(item => item.trim() !== '');
      }
      
      const data: OnboardingDataInsert = {
        user_id: userId,
        payment_options: paymentOptions as string[],
        tax_id: values.tax_id,
        domain: values.domain,
        target_audience: values.target_audience,
        ideal_customer: values.ideal_customer,
        qualification_questions: values.qualification_questions,
        color_scheme: values.color_scheme,
        company_info: values.company_info,
        faq: faqItems as string[],
        website_examples: values.website_examples,
        competitor_websites: values.competitor_websites,
        address: values.address,
        has_completed_onboarding: true,
        updated_at: new Date().toISOString()
      };
      
      const { error } = await supabase
        .from('onboarding_data')
        .upsert(data, { 
          onConflict: 'user_id',
          ignoreDuplicates: false
        });
      
      if (error) {
        throw error;
      }
      
      toast.success(initialData ? "Onboarding-Daten aktualisiert" : "Onboarding abgeschlossen");
      
      onOpenChange(false);
      
      if (onComplete) {
        onComplete();
      }
      
      if (isRegistration) {
        navigate("/");
      }
    } catch (error) {
      console.error("Fehler beim Speichern der Onboarding-Daten:", error);
      toast.error("Fehler beim Speichern. Bitte versuchen Sie es später erneut.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderField = (fieldName: string) => {
    switch (fieldName) {
      case "payment_options":
        return (
          <FormField
            key={fieldName}
            control={form.control}
            name={fieldName}
            render={({ field }) => (
              <FormItem>
                <FormLabel>Zahlungsoptionen</FormLabel>
                <FormControl>
                  <Textarea 
                    placeholder="Barzahlung, Kreditkarte, PayPal, Überweisung, etc. (durch Komma getrennt)"
                    className="min-h-[100px]"
                    {...field}
                    value={Array.isArray(field.value) ? field.value.join(', ') : field.value}
                    onChange={(e) => field.onChange(e.target.value)}
                  />
                </FormControl>
                <FormDescription>
                  Geben Sie Ihre Zahlungsoptionen durch Kommas getrennt ein.
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
        );
      case "tax_id":
        return (
          <FormField
            key={fieldName}
            control={form.control}
            name={fieldName}
            render={({ field }) => (
              <FormItem>
                <FormLabel>Steuernummer</FormLabel>
                <FormControl>
                  <Input placeholder="Ihre Steuernummer oder USt-IdNr." {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        );
      case "domain":
        return (
          <FormField
            key={fieldName}
            control={form.control}
            name={fieldName}
            render={({ field }) => (
              <FormItem>
                <FormLabel>Domain</FormLabel>
                <FormControl>
                  <Input placeholder="www.ihre-domain.de" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        );
      case "target_audience":
        return (
          <FormField
            key={fieldName}
            control={form.control}
            name={fieldName}
            render={({ field }) => (
              <FormItem>
                <FormLabel>Zielgruppe</FormLabel>
                <FormControl>
                  <Textarea 
                    placeholder="Beschreiben Sie Ihre Zielgruppe"
                    className="min-h-[100px]"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        );
      case "ideal_customer":
        return (
          <FormField
            key={fieldName}
            control={form.control}
            name={fieldName}
            render={({ field }) => (
              <FormItem>
                <FormLabel>Idealer Kunde</FormLabel>
                <FormControl>
                  <Textarea 
                    placeholder="Beschreiben Sie Ihren idealen Kunden"
                    className="min-h-[100px]"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        );
      case "qualification_questions":
        return (
          <FormField
            key={fieldName}
            control={form.control}
            name={fieldName}
            render={({ field }) => (
              <FormItem>
                <FormLabel>Qualifikationsfragen</FormLabel>
                <FormControl>
                  <Textarea 
                    placeholder="Welche Fragen sollten potenzielle Kunden beantworten?"
                    className="min-h-[150px]"
                    {...field}
                  />
                </FormControl>
                <FormDescription>
                  Diese Fragen helfen Ihnen, geeignete Kunden zu identifizieren.
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
        );
      case "color_scheme":
        return (
          <FormField
            key={fieldName}
            control={form.control}
            name={fieldName}
            render={({ field }) => (
              <FormItem>
                <FormLabel>Farbschema</FormLabel>
                <FormControl>
                  <Input placeholder="z.B. #FF5733, Blau/Grün, Corporate Colors..." {...field} />
                </FormControl>
                <FormDescription>
                  Bevorzugte Farben für Ihre Webseite oder Corporate Design.
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
        );
      case "company_info":
        return (
          <FormField
            key={fieldName}
            control={form.control}
            name={fieldName}
            render={({ field }) => (
              <FormItem>
                <FormLabel>Unternehmensinformationen</FormLabel>
                <FormControl>
                  <Textarea 
                    placeholder="Beschreiben Sie Ihr Unternehmen, Ihre Dienstleistungen oder Produkte"
                    className="min-h-[150px]"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        );
      case "faq":
        return (
          <FormField
            key={fieldName}
            control={form.control}
            name={fieldName}
            render={({ field }) => (
              <FormItem>
                <FormLabel>Häufig gestellte Fragen (FAQ)</FormLabel>
                <FormControl>
                  <Textarea 
                    placeholder="Frage: Wie lange dauert die Lieferung?\n\nAntwort: In der Regel 2-3 Werktage.\n\nFrage: Gibt es Rabatte?\n\nAntwort: Ja, bei größeren Bestellungen."
                    className="min-h-[200px]"
                    {...field}
                    value={Array.isArray(field.value) ? field.value.join('\n\n') : field.value}
                    onChange={(e) => field.onChange(e.target.value)}
                  />
                </FormControl>
                <FormDescription>
                  Geben Sie Fragen und Antworten ein. Trennen Sie jede Frage/Antwort-Kombination durch eine Leerzeile.
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
        );
      case "website_examples":
        return (
          <FormField
            key={fieldName}
            control={form.control}
            name={fieldName}
            render={({ field }) => (
              <FormItem>
                <FormLabel>Beispiel-Webseiten</FormLabel>
                <FormControl>
                  <Textarea 
                    placeholder="URLs von Webseiten, die Ihnen gefallen"
                    className="min-h-[100px]"
                    {...field}
                  />
                </FormControl>
                <FormDescription>
                  Webseiten, deren Design oder Funktionalität Ihnen gefällt.
                </FormDescription>
                <FormMessage />
              </FormItem>
            )}
          />
        );
      case "competitor_websites":
        return (
          <FormField
            key={fieldName}
            control={form.control}
            name={fieldName}
            render={({ field }) => (
              <FormItem>
                <FormLabel>Wettbewerber-Webseiten</FormLabel>
                <FormControl>
                  <Textarea 
                    placeholder="URLs von Webseiten Ihrer Wettbewerber"
                    className="min-h-[100px]"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        );
      case "address":
        return (
          <FormField
            key={fieldName}
            control={form.control}
            name={fieldName}
            render={({ field }) => (
              <FormItem>
                <FormLabel>Adresse</FormLabel>
                <FormControl>
                  <Textarea 
                    placeholder="Ihre Geschäftsadresse"
                    className="min-h-[100px]"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        );
      default:
        return null;
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {initialData ? "Onboarding-Daten bearbeiten" : "Willkommen bei Hoss Immo"}
          </DialogTitle>
          <DialogDescription>
            {initialData
              ? "Bearbeiten Sie Ihre Onboarding-Informationen"
              : "Bitte vervollständigen Sie diese kurze Umfrage, um Ihr Konto einzurichten"}
          </DialogDescription>
        </DialogHeader>
        
        <div className="flex gap-2 mb-6 overflow-x-auto p-1">
          {steps.map((step, index) => (
            <div 
              key={index}
              className={`flex items-center ${index <= currentStep ? "text-primary" : "text-muted-foreground"}`}
            >
              <div className="flex items-center">
                {index < currentStep ? (
                  <CheckCircle className="h-5 w-5 mr-1" />
                ) : index === currentStep ? (
                  <Circle className="h-5 w-5 mr-1 fill-primary" />
                ) : (
                  <CircleDashed className="h-5 w-5 mr-1" />
                )}
                <span className={`text-xs whitespace-nowrap ${index === currentStep ? "font-medium" : ""}`}>
                  {step.title}
                </span>
              </div>
              {index < steps.length - 1 && (
                <div className={`h-[1px] w-4 mx-1 ${index < currentStep ? "bg-primary" : "bg-muted-foreground"}`}></div>
              )}
            </div>
          ))}
        </div>
        
        <Form {...form}>
          <form className="space-y-6">
            <h3 className="text-lg font-medium">{steps[currentStep].title}</h3>
            <p className="text-sm text-muted-foreground mb-4">{steps[currentStep].description}</p>
            
            <div className="space-y-4">
              {steps[currentStep].fields.map(fieldName => renderField(fieldName))}
            </div>
          </form>
        </Form>
        
        <DialogFooter className="flex justify-between mt-6">
          <div>
            {currentStep > 0 && (
              <Button
                type="button"
                variant="outline"
                onClick={handlePrevious}
                disabled={isSubmitting}
              >
                Zurück
              </Button>
            )}
          </div>
          <div>
            {!isRegistration && (
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                className="mr-2"
                disabled={isSubmitting}
              >
                <X className="mr-1 h-4 w-4" />
                Abbrechen
              </Button>
            )}
            <Button
              type="button"
              onClick={handleNext}
              disabled={isSubmitting}
            >
              {isSubmitting ? "Wird gespeichert..." : currentStep === steps.length - 1 ? "Speichern" : "Weiter"}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default OnboardingForm;
